import React, { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import { Speed, ArrowForward, CheckCircle, Email } from '@mui/icons-material';
import { sendEmail } from '../utils/email';

// Migration Decision Tool Component
interface Question {
  id: string;
  text: string;
  options: {
    text: string;
    value: string;
    nextQuestion?: string;
  }[];
}

interface Answer {
  questionId: string;
  value: string;
}

const questions: Question[] = [
  {
    id: 'purpose',
    text: 'What is the primary purpose of your application?',
    options: [
      { text: 'Single Page Application (SPA)', value: 'spa', nextQuestion: 'complexity' },
      { text: 'Marketing/Content Website', value: 'marketing', nextQuestion: 'seo' },
      { text: 'E-commerce Platform', value: 'ecommerce', nextQuestion: 'fullstack' },
      { text: 'Dashboard/Admin Panel', value: 'dashboard', nextQuestion: 'complexity' },
      { text: 'Blog/Documentation', value: 'blog', nextQuestion: 'seo' },
    ],
  },
  {
    id: 'seo',
    text: 'How important is SEO and initial page load performance?',
    options: [
      { text: 'Critical - Need excellent SEO', value: 'critical', nextQuestion: 'fullstack' },
      { text: 'Important - Some SEO needed', value: 'important', nextQuestion: 'fullstack' },
      { text: 'Not important - Internal tool', value: 'not-important', nextQuestion: 'complexity' },
    ],
  },
  {
    id: 'fullstack',
    text: 'Do you need backend functionality (API routes, database, authentication)?',
    options: [
      { text: 'Yes - Full-stack features needed', value: 'yes', nextQuestion: 'deployment' },
      { text: 'Maybe - Might need in the future', value: 'maybe', nextQuestion: 'performance' },
      { text: 'No - Frontend only', value: 'no', nextQuestion: 'performance' },
    ],
  },
  {
    id: 'complexity',
    text: 'How complex is your application?',
    options: [
      { text: 'Simple - Few components, basic features', value: 'simple', nextQuestion: 'performance' },
      { text: 'Medium - Multiple pages, some state management', value: 'medium', nextQuestion: 'performance' },
      { text: 'Complex - Large codebase, advanced features', value: 'complex', nextQuestion: 'team' },
    ],
  },
  {
    id: 'performance',
    text: 'How important is development speed and hot reload performance?',
    options: [
      { text: 'Critical - Development speed is priority', value: 'critical', nextQuestion: 'team' },
      { text: 'Important - Faster is better', value: 'important', nextQuestion: 'team' },
      { text: 'Not critical - Can wait a bit longer', value: 'not-critical', nextQuestion: 'deployment' },
    ],
  },
  {
    id: 'team',
    text: 'What is your team\'s experience level?',
    options: [
      { text: 'Beginner - New to React ecosystem', value: 'beginner', nextQuestion: 'timeline' },
      { text: 'Intermediate - Some React experience', value: 'intermediate', nextQuestion: 'timeline' },
      { text: 'Advanced - Experienced with React tooling', value: 'advanced', nextQuestion: 'deployment' },
    ],
  },
  {
    id: 'timeline',
    text: 'How much time can you dedicate to the migration?',
    options: [
      { text: 'Minimal - Just a few hours', value: 'minimal', nextQuestion: 'deployment' },
      { text: 'Moderate - 1-2 days', value: 'moderate', nextQuestion: 'deployment' },
      { text: 'Flexible - Can take a week if needed', value: 'flexible', nextQuestion: 'deployment' },
    ],
  },
  {
    id: 'deployment',
    text: 'Where do you plan to deploy your application?',
    options: [
      { text: 'Static hosting (Netlify, Vercel, S3)', value: 'static' },
      { text: 'Vercel (with serverless functions)', value: 'vercel' },
      { text: 'Traditional server/VPS', value: 'server' },
      { text: 'Not sure yet', value: 'unsure' },
    ],
  },
];

interface MigrationDecisionToolProps {
  onNavigateToGuide: (framework: 'vite' | 'nextjs') => void;
}

export function MigrationDecisionTool({ onNavigateToGuide }: MigrationDecisionToolProps) {
  const [currentQuestionId, setCurrentQuestionId] = useState<string>('purpose');
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [recommendation, setRecommendation] = useState<'vite' | 'nextjs' | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const currentQuestion = questions.find(q => q.id === currentQuestionId);

  const handleAnswer = (value: string) => {
    const newAnswers = [...answers.filter(a => a.questionId !== currentQuestionId), {
      questionId: currentQuestionId,
      value
    }];
    setAnswers(newAnswers);

    const option = currentQuestion?.options.find(o => o.value === value);
    if (option?.nextQuestion) {
      setCurrentQuestionId(option.nextQuestion);
    } else {
      // Calculate recommendation
      const recommendation = calculateRecommendation(newAnswers);
      setRecommendation(recommendation);
      setShowResults(true);
    }
  };

  const calculateRecommendation = (answers: Answer[]): 'vite' | 'nextjs' => {
    let viteScore = 0;
    let nextjsScore = 0;

    answers.forEach(answer => {
      switch (answer.questionId) {
        case 'purpose':
          if (['spa', 'dashboard'].includes(answer.value)) viteScore += 2;
          if (['marketing', 'ecommerce', 'blog'].includes(answer.value)) nextjsScore += 2;
          break;
        case 'seo':
          if (answer.value === 'critical') nextjsScore += 3;
          if (answer.value === 'important') nextjsScore += 2;
          if (answer.value === 'not-important') viteScore += 2;
          break;
        case 'fullstack':
          if (answer.value === 'yes') nextjsScore += 3;
          if (answer.value === 'maybe') nextjsScore += 1;
          if (answer.value === 'no') viteScore += 2;
          break;
        case 'complexity':
          if (answer.value === 'simple') viteScore += 2;
          if (answer.value === 'complex') nextjsScore += 1;
          break;
        case 'performance':
          if (['critical', 'important'].includes(answer.value)) viteScore += 2;
          break;
        case 'team':
          if (answer.value === 'beginner') viteScore += 2;
          if (answer.value === 'advanced') nextjsScore += 1;
          break;
        case 'timeline':
          if (answer.value === 'minimal') viteScore += 2;
          if (answer.value === 'flexible') nextjsScore += 1;
          break;
        case 'deployment':
          if (answer.value === 'static') viteScore += 1;
          if (answer.value === 'vercel') nextjsScore += 2;
          if (answer.value === 'server') nextjsScore += 1;
          break;
      }
    });

    return viteScore >= nextjsScore ? 'vite' : 'nextjs';
  };

  const resetQuiz = () => {
    setCurrentQuestionId('purpose');
    setAnswers([]);
    setRecommendation(null);
    setShowResults(false);
    setEmailAddress('');
  };

  const handleEmailResult = async () => {
    if (!emailAddress.trim()) {
      setSnackbarMessage('Please enter a valid email address');
      setSnackbarOpen(true);
      return;
    }

    setEmailSending(true);
    try {
      const questionText = answers.map(answer => {
        const question = questions.find(q => q.id === answer.questionId);
        const option = question?.options.find(o => o.value === answer.value);
        return `${question?.text}\nAnswer: ${option?.text}`;
      }).join('\n\n');

      const emailBody = `Migration Decision Tool Results
      
Your Answers:
${questionText}

Recommendation: ${recommendation === 'vite' ? 'Vite' : 'Next.js'}

${recommendation === 'vite' ? 
  'Vite is recommended for your project because it offers lightning-fast development with instant hot reload, minimal configuration, and is perfect for SPAs that don\'t need SSR.' :
  'Next.js is recommended for your project because it provides built-in SSR/SSG for excellent SEO, API routes for full-stack development, and advanced optimizations out of the box.'
}

Best regards,
Migration Tool Team`;

      await sendEmail({
        to: emailAddress,
        subject: `Migration Recommendation: ${recommendation === 'vite' ? 'Vite' : 'Next.js'}`,
        title: 'Your Migration Decision Results',
        body: emailBody,
      });

      setSnackbarMessage('Results sent successfully!');
      setEmailDialogOpen(false);
      setEmailAddress('');
    } catch (error) {
      setSnackbarMessage('Failed to send email. Please try again.');
    } finally {
      setEmailSending(false);
      setSnackbarOpen(true);
    }
  };

  const goToPrevious = () => {
    // Find the previous question based on answers
    const currentIndex = answers.findIndex(a => {
      const question = questions.find(q => q.id === a.questionId);
      const option = question?.options.find(o => o.value === a.value);
      return option?.nextQuestion === currentQuestionId;
    });
    
    if (currentIndex >= 0) {
      const prevAnswer = answers[currentIndex];
      setCurrentQuestionId(prevAnswer.questionId);
      setAnswers(answers.slice(0, currentIndex));
    } else if (answers.length > 0) {
      setCurrentQuestionId(answers[answers.length - 1].questionId);
      setAnswers(answers.slice(0, -1));
    }
  };

  if (showResults && recommendation) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Recommendation: {recommendation === 'vite' ? 'Vite' : 'Next.js'}</AlertTitle>
          Based on your answers, we recommend migrating to {recommendation === 'vite' ? 'Vite' : 'Next.js'}.
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            {recommendation === 'vite' ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  <Speed sx={{ mr: 1, color: 'primary.main' }} />
                  Vite Migration Recommended
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Vite is perfect for your use case because:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="Lightning-fast development server with instant hot reload" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="Minimal configuration required - easy migration" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="Great for SPAs and applications that don't need SSR" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="Excellent development experience with modern tooling" />
                  </ListItem>
                </List>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  <ArrowForward sx={{ mr: 1, color: 'primary.main' }} />
                  Next.js Migration Recommended
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Next.js is ideal for your requirements because:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="Built-in SSR/SSG for excellent SEO and performance" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="API routes for full-stack development" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="Advanced optimizations out of the box" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary="Perfect for content-driven and e-commerce sites" />
                  </ListItem>
                </List>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            onClick={resetQuiz}
          >
            Restart
          </Button>
          <Button 
            variant="outlined"
            startIcon={<Email />}
            onClick={() => setEmailDialogOpen(true)}
          >
            Email Result
          </Button>
          <Button 
            variant="outlined"
            onClick={() => onNavigateToGuide(recommendation)}
          >
            View {recommendation === 'vite' ? 'Vite' : 'Next.js'} Migration Guide
          </Button>
        </Box>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Email Migration Results</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              We'll send your migration recommendation and all your answers to your email address.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="your-email@example.com"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEmailResult} 
              variant="contained"
              disabled={emailSending}
            >
              {emailSending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Answer a few questions to get a personalized migration recommendation:
      </Typography>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Question {answers.length + 1} of {questions.length}
            </Typography>
            <Box sx={{ width: '100%', mt: 1 }}>
              <Box 
                sx={{ 
                  width: `${((answers.length + 1) / questions.length) * 100}%`,
                  height: 4,
                  backgroundColor: 'primary.main',
                  borderRadius: 2,
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
          </Box>

          {currentQuestion && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {currentQuestion.text}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {currentQuestion.options.map((option, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    onClick={() => handleAnswer(option.value)}
                    sx={{ 
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 1.5,
                      px: 2
                    }}
                  >
                    {option.text}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {answers.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="text"
                onClick={goToPrevious}
                size="small"
              >
                ← Previous Question
              </Button>            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
